import { useEffect, useRef, useState, useCallback } from 'react';
import { consultarEstadoPedido } from 'scripts/consultarEstadoPedido';

/**
 * Hook personalizado para hacer polling del estado de un pedido
 * Útil para detectar cuando MercadoPago procesa el pago vía webhook
 *
 * @param {number} pedidoId - ID del pedido a monitorear
 * @param {string} token - Token de autenticación
 * @param {Object} options - Configuración del polling
 * @param {boolean} options.enabled - Si el polling está activo
 * @param {number} options.interval - Intervalo en ms entre consultas (default: 5000)
 * @param {number} options.timeout - Timeout total en ms (default: 600000 - 10 min)
 * @param {Function} options.onPagado - Callback cuando el pedido es pagado
 * @param {Function} options.onError - Callback para errores
 * @param {Function} options.onTimeout - Callback cuando se alcanza el timeout
 *
 * @returns {Object} - { estadoPedido, isPagado, isPolling, error, stopPolling }
 */
export const usePaymentPolling = (pedidoId, token, options = {}) => {
	const {
		enabled = true,
		interval = 5000, // 5 segundos
		timeout = 600000, // 10 minutos
		onPagado = null,
		onError = null,
		onTimeout = null,
	} = options;

	const [estadoPedido, setEstadoPedido] = useState(null);
	const [isPagado, setIsPagado] = useState(false);
	const [isPolling, setIsPolling] = useState(false);
	const [error, setError] = useState(null);

	const intervalRef = useRef(null);
	const timeoutRef = useRef(null);
	const startTimeRef = useRef(null);
	const isMountedRef = useRef(true);
	const hasDetectedPaymentRef = useRef(false); // Nuevo: rastrear si ya detectamos un pago

	// Refs para callbacks (evitar loop infinito por recreación de funciones)
	const onPagadoRef = useRef(onPagado);
	const onErrorRef = useRef(onError);
	const onTimeoutRef = useRef(onTimeout);

	// Actualizar refs cuando cambien los callbacks
	useEffect(() => {
		onPagadoRef.current = onPagado;
		onErrorRef.current = onError;
		onTimeoutRef.current = onTimeout;
	}, [onPagado, onError, onTimeout]);

	// Función para detener el polling manualmente
	const stopPolling = useCallback(() => {
		if (intervalRef.current) {
			clearInterval(intervalRef.current);
			intervalRef.current = null;
		}
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current);
			timeoutRef.current = null;
		}
		setIsPolling(false);
	}, []);

	// Función para consultar el estado
	const checkPaymentStatus = useCallback(async () => {
		if (!pedidoId || !token) {
			return;
		}

		try {
			const data = await consultarEstadoPedido(pedidoId, token);

			if (!isMountedRef.current) return;

			setEstadoPedido(data);

			// Estados de pedido:
			// 1 = EN_PROGRESO (pendiente de pago)
			// 2 = PAGADO
			// 3+ = otros estados (ENTREGADO, CANCELADO, etc.)

			// Considerar "pagado" solo cuando el pago está APROBADO
			if (data.estado === 2 && data.estado_pago === 'APROBADO' && !hasDetectedPaymentRef.current) {
				console.log('✅ Pago APROBADO detectado.');
				hasDetectedPaymentRef.current = true;
				setIsPagado(true);
				stopPolling();

				if (onPagadoRef.current) {
					onPagadoRef.current(data);
				}
				return;
			}

			// Si queda pendiente/en proceso, detener polling.
			// El seguimiento continúa en Mis Pedidos para evitar depender del carrito.
			if (data.estado === 2 && data.estado_pago === 'PENDIENTE') {
				console.log('⌛ Pago pendiente/en proceso. Se detiene polling y continúa seguimiento en Mis Pedidos.');
				stopPolling();
				return;
			}

			// Si el pago fue rechazado, es estado terminal para el intento actual.
			if (data.estado === 2 && data.estado_pago === 'RECHAZADO') {
				console.log('❌ Pago rechazado. Se detiene polling.');
				stopPolling();
				return;
			}

			// Si pedido avanza a otros estados no recuperables desde carrito, detener polling
			if (data.estado !== 1 && data.estado !== 2) {
				console.log('ℹ️ Pedido en estado final no pagable desde carrito:', data.estado);
				stopPolling();
				return;
			}

			console.log('🔄 Pedido en progreso.');
		} catch (err) {
			console.error('Error en polling de pago:', err);

			if (!isMountedRef.current) return;

			setError(err.message);

			// Si es error de sesión, detener polling
			if (err.message === 'SESION_EXPIRADA') {
				stopPolling();
			}

			// Llamar callback de error si existe (usando ref)
			if (onErrorRef.current) {
				onErrorRef.current(err);
			}
		}
	}, [pedidoId, token, stopPolling]);

	// Efecto principal: iniciar/detener polling
	useEffect(() => {
		// Reset en cada cambio de pedidoId o habilitación
		isMountedRef.current = true;

		// Solo resetear hasDetectedPaymentRef si cambió el pedidoId
		// (no en cada re-render por otros cambios)
		hasDetectedPaymentRef.current = false;
		setIsPagado(false);
		setError(null);

		// Solo iniciar polling si está habilitado y tenemos pedidoId
		if (!enabled || !pedidoId || !token) {
			setIsPolling(false);
			return;
		}

		console.log('🔄 Iniciando polling para pedido:', pedidoId);
		setIsPolling(true);
		startTimeRef.current = Date.now();

		// Primera consulta inmediata
		checkPaymentStatus();

		// Configurar intervalo para consultas periódicas
		intervalRef.current = setInterval(checkPaymentStatus, interval);

		// Configurar timeout máximo
		timeoutRef.current = setTimeout(() => {
			console.log('⏱️ Timeout de polling alcanzado');
			stopPolling();

			// Llamar callback de timeout si existe (usando ref)
			if (onTimeoutRef.current) {
				onTimeoutRef.current();
			}
		}, timeout);

		// Cleanup al desmontar o cambiar dependencias
		return () => {
			isMountedRef.current = false;
			stopPolling();
		};
	}, [enabled, pedidoId, token, interval, timeout, checkPaymentStatus, stopPolling]);

	return {
		estadoPedido,
		isPagado,
		isPolling,
		error,
		stopPolling, // Permitir detener manualmente si es necesario
	};
};
